import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight, Search, Grid, List, Sparkles, ArrowRight, Loader2, Play
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { fetchGalleryItems, type GalleryItem } from '@/lib/cms';

const GRADIENT_PALETTES = [
  'from-[#635bff] to-[#00d4ff]',
  'from-[#00d4ff] to-[#00ff88]',
  'from-[#a855f7] to-[#ec4899]',
  'from-[#ff6b6b] to-[#ffa500]',
  'from-[#635bff] to-[#a855f7]',
  'from-[#00d4ff] to-[#635bff]',
];

const GalleryShowcase: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadGallery() {
      try {
        setLoading(true);
        setError(null);
        const items = await fetchGalleryItems(100);
        if (cancelled) return;
        setGalleryItems(items);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load gallery');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadGallery();
    return () => { cancelled = true; };
  }, []);

  const filteredItems = galleryItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.format_type.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#635bff]/20 via-transparent to-[#00d4ff]/10" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#635bff]/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#00d4ff]/15 rounded-full blur-3xl animate-float-delayed" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 mb-6">
            <Link to="/resources" className="hover:text-slate-900 dark:hover:text-white transition-colors">Resources</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 dark:text-white">Gallery</span>
          </div>

          <div className="max-w-4xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-[#635bff] bg-[#635bff]/10 border border-[#635bff]/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              Interactive Ad Gallery
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight">
              Ad Gallery
            </h1>
            <p className="mt-6 text-xl text-slate-600 dark:text-slate-400">
              Explore our collection of interactive ad examples showcasing different formats and creative approaches.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-y border-slate-200 dark:border-white/10 sticky top-0 bg-white/95 dark:bg-[#0a0a0f]/95 backdrop-blur-sm z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
                <input
                  type="text"
                  placeholder="Search gallery..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#635bff]/50 w-64"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600 dark:text-slate-400">{filteredItems.length} items</span>
              <div className="flex bg-slate-100 dark:bg-white/5 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-[#635bff] text-white' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-[#635bff] text-white' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="w-8 h-8 text-[#635bff] animate-spin" />
              <span className="ml-3 text-slate-600 dark:text-slate-400">Loading gallery...</span>
            </div>
          ) : error ? (
            <div className="text-center py-32">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-[#635bff] text-white rounded-lg hover:bg-[#635bff]/90 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-32">
              <p className="text-slate-600 dark:text-slate-400">No gallery items found.</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredItems.map((item, idx) => (
                <div
                  key={item.id}
                  className="group bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden hover:border-[#635bff]/30 transition-all"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {item.featured_image ? (
                      <img
                        src={item.featured_image}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className={`w-full h-full bg-gradient-to-br ${GRADIENT_PALETTES[idx % GRADIENT_PALETTES.length]} flex items-center justify-center p-4`}>
                        <span className="text-white/90 text-sm font-semibold text-center line-clamp-3">{item.title}</span>
                      </div>
                    )}
                    {item.demo_url && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                          <a
                            href={item.demo_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-2 py-2 bg-[#635bff] text-white font-medium rounded-lg hover:bg-[#5449e6] transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Play className="w-4 h-4" />
                            View Demo
                          </a>
                        </div>
                      </div>
                    )}
                    {item.format_type && (
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 text-xs font-medium text-white bg-black/50 backdrop-blur-sm rounded-full">
                          {item.format_type}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] transition-colors">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                        {item.description}
                      </p>
                    )}
                    {item.tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {item.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-white/5 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredItems.map((item, idx) => (
                <div
                  key={item.id}
                  className="group flex items-center gap-6 p-4 bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-xl hover:border-[#635bff]/30 transition-all"
                >
                  <div className="w-32 h-24 rounded-lg overflow-hidden flex-shrink-0">
                    {item.featured_image ? (
                      <img
                        src={item.featured_image}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className={`w-full h-full bg-gradient-to-br ${GRADIENT_PALETTES[idx % GRADIENT_PALETTES.length]} flex items-center justify-center p-2`}>
                        <span className="text-white/90 text-xs font-semibold text-center line-clamp-2">{item.title}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] transition-colors">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{item.description}</p>
                    )}
                    <div className="mt-2 flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                      {item.format_type && <span>{item.format_type}</span>}
                      {item.tags.length > 0 && <span>{item.tags.join(', ')}</span>}
                    </div>
                  </div>

                  {item.demo_url && (
                    <a
                      href={item.demo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 text-white bg-[#635bff] rounded-lg hover:bg-[#5449e6] transition-colors flex-shrink-0"
                    >
                      View Demo
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GalleryShowcase;
